/*
  Product gallery (site-behaviour-spec.md > Product gallery and Video).
  - Swipe (scroll snap), arrows and thumbnails step through every slide; counter reads n / total.
  - Videos: muted, inline, play while their slide is showing and on screen, pause otherwise.
    "Play once and hold" stops on the last frame. Desktop-only clips show their poster on mobile.
    Sound follows video-player.js (BBSound): muted unless the visitor has interacted, pill and toggle on clips with audio.
    Reduced motion: posters only, no autoplay.
  - Full-screen view: same square frame sized to the screen, strip of all thumbnails, arrows,
    swipe and arrow keys. Esc or the close button leaves the page gallery on the last slide viewed.
  - Listens for "variant:change": swaps slide 1 to the chosen colour's linked image (map from product-gallery.liquid),
    with the cut-out look when its file name contains "cutout", and shows it.
*/
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.counter = this.querySelector('[data-current]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.badge = this.querySelector('[data-badge]');
    this.lightbox = this.querySelector('[data-lightbox]');
    this.index = 0;
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Any gallery interaction counts as the visitor interacting, so clips with audio may play with sound
    const touched = () => window.BBSound?.markInteracted();
    this.prev?.addEventListener('click', () => { touched(); this.go(this.index - 1); });
    this.next?.addEventListener('click', () => { touched(); this.go(this.index + 1); });
    this.thumbs.forEach((t) => t.addEventListener('click', () => { touched(); this.go(Number(t.dataset.thumb)); }));
    this.querySelectorAll('[data-open-lightbox]').forEach((b) => b.addEventListener('click', () => { touched(); this.openLightbox(); }));
    this.track.addEventListener('touchstart', touched, { passive: true });
    // Touch screens: the zoom overlay lets swipes through, so a tap on the slide opens full screen instead
    // (a swipe fires no click). Buttons on a slide, like the sound controls, keep their own job.
    this.track.addEventListener('click', (e) => {
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches || e.target.closest('button, a')) return;
      touched();
      this.openLightbox();
    });
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
    });

    let scrollTimer;
    this.track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (i !== this.index) this.setIndex(i);
      }, 80);
    }, { passive: true });

    this.setupVideos();
    const map = this.querySelector('[data-colour-slides]');
    try { this.colourSlides = map ? JSON.parse(map.textContent) : null; } catch (err) { this.colourSlides = null; }
    document.addEventListener('variant:change', (e) => {
      if (this.swapSlideOne(e.detail?.variantId)) { this.go(0); return; }
      const id = e.detail?.mediaId;
      if (!id) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1) this.go(i);
    });
    this.setIndex(0);
  }

  // Slide 1 follows the chosen colour: main image, its thumbnail and its full-screen thumbnail
  swapSlideOne(variantId) {
    const one = this.colourSlides && this.colourSlides[String(variantId)];
    const img = this.slides[0]?.querySelector('img');
    if (!one || !img) return false;
    // The colour's own cut-out is in the gallery: it trades places with slide 1 (slide, thumbnail, full-screen thumbnail)
    if (one.media) {
      const k = this.slides.findIndex((s) => s.dataset.mediaId === String(one.media));
      if (k > 0) this.trade(k);
      return k > -1;
    }
    if (img.getAttribute('src') !== one.src) {
      img.setAttribute('srcset', one.srcset);
      img.setAttribute('src', one.src);
      img.setAttribute('alt', one.alt);
      if (one.width) img.setAttribute('width', one.width);
      if (one.height) img.setAttribute('height', one.height);
      const thumb = this.querySelector('[data-thumb="0"] img');
      if (thumb) { thumb.setAttribute('src', one.thumb); thumb.removeAttribute('srcset'); }
      const mini = this.querySelector('[data-lb-thumb="0"] img');
      if (mini) { mini.setAttribute('src', one.mini); mini.removeAttribute('srcset'); }
      this.slides[0].classList.toggle('is-cutout', one.cutout);
      this.slides[0].classList.toggle('is-fill', !one.cutout);
      this.querySelector('[data-thumb="0"]')?.classList.toggle('is-cutout', one.cutout);
    }
    return true;
  }

  trade(k) {
    const pairs = [
      [this.slides[0], this.slides[k]],
      [this.querySelector('[data-thumb="0"]'), this.querySelector(`[data-thumb="${k}"]`)],
      [this.querySelector('[data-lb-thumb="0"]'), this.querySelector(`[data-lb-thumb="${k}"]`)],
    ];
    pairs.forEach(([a, b]) => {
      if (!a) return;
      const aImg = a.querySelector('img');
      const bImg = b ? b.querySelector('img') : null;
      if (!aImg || !bImg) return;
      const holder = aImg.cloneNode(true);
      aImg.replaceWith(bImg.cloneNode(true));
      bImg.replaceWith(holder);
      const aCut = a.classList.contains('is-cutout');
      a.classList.toggle('is-cutout', b.classList.contains('is-cutout'));
      b.classList.toggle('is-cutout', aCut);
      if (a.matches('[data-slide]')) {
        a.classList.toggle('is-fill', !a.classList.contains('is-cutout'));
        b.classList.toggle('is-fill', !b.classList.contains('is-cutout'));
        [a.dataset.mediaId, b.dataset.mediaId] = [b.dataset.mediaId, a.dataset.mediaId];
      }
    });
  }

  go(i) {
    // Arrows wrap: previous from slide 1 goes to the last slide, next from the last goes to slide 1
    const n = (i + this.slides.length) % this.slides.length;
    this.track.scrollTo({ left: n * this.track.clientWidth, behavior: this.reduced.matches ? 'auto' : 'smooth' });
    this.setIndex(n);
  }

  setIndex(i) {
    this.index = i;
    if (this.counter) this.counter.textContent = i + 1;
    if (this.prev) this.prev.hidden = this.mobile.matches && i === 0; // board: mobile has no previous arrow on slide 1
    if (this.badge) this.badge.hidden = i !== 0; // "Our top pick" on slide 1 only
    this.thumbs.forEach((t) => t.classList.toggle('is-active', Number(t.dataset.thumb) === i));
    this.updateVideos();
  }

  /* ---------- Video ---------- */
  setupVideos() {
    this.videos = [...this.querySelectorAll('.gallery__video')];
    if (!this.videos.length) return;
    const useMobile = () => this.videos.forEach((v) => {
      if (!v.dataset.desktopSrc) v.dataset.desktopSrc = v.getAttribute('src');
      const want = this.mobile.matches && v.dataset.mobileSrc ? v.dataset.mobileSrc : v.dataset.desktopSrc;
      if (v.getAttribute('src') !== want) v.setAttribute('src', want);
      v.loop = 'loop' in v.dataset;
    });
    useMobile();
    this.videos.forEach((v) => window.BBSound?.bind(v.closest('[data-slide]'), v));
    this.mobile.addEventListener('change', () => { useMobile(); this.setIndex(this.index); });
    this.visible = false;
    new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
      this.updateVideos();
    }, { threshold: 0.4 }).observe(this);
  }

  updateVideos() {
    (this.videos || []).forEach((v) => {
      const slide = v.closest('[data-slide]');
      const active = Number(slide.dataset.index) === this.index;
      const allowed = !this.reduced.matches && !(this.mobile.matches && 'desktopOnly' in v.dataset);
      if (active && this.visible && allowed) {
        if (window.BBSound) window.BBSound.play(v); else v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }

  /* ---------- Full-screen view ---------- */
  openLightbox() {
    if (!this.lightbox) return;
    const lb = this.lightbox;
    this.lbIndex = this.index;
    if (!lb.dataset.ready) {
      lb.querySelector('[data-close-lightbox]').addEventListener('click', () => lb.close());
      lb.querySelector('[data-lb-prev]').addEventListener('click', () => this.lbGo(this.lbIndex - 1));
      lb.querySelector('[data-lb-next]').addEventListener('click', () => this.lbGo(this.lbIndex + 1));
      lb.querySelectorAll('[data-lb-thumb]').forEach((t) => t.addEventListener('click', () => this.lbGo(Number(t.dataset.lbThumb))));
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.lbGo(this.lbIndex + 1);
        if (e.key === 'ArrowLeft') this.lbGo(this.lbIndex - 1);
      });
      let startX = null;
      const frame = lb.querySelector('[data-lb-frame]');
      frame.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      frame.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 40) this.lbGo(this.lbIndex + (dx < 0 ? 1 : -1));
        startX = null;
      });
      lb.addEventListener('close', () => {
        lb.querySelector('[data-lb-frame]').innerHTML = '';
        document.documentElement.classList.remove('drawer-open');
        this.go(this.lbIndex);
      });
      lb.dataset.ready = 'true';
    }
    lb.querySelectorAll('.lightbox__strip img').forEach((img) => { img.loading = 'eager'; });
    lb.showModal();
    document.documentElement.classList.add('drawer-open');
    this.updateVideos();
    this.lbGo(this.lbIndex);
  }

  lbGo(i) {
    const n = (i + this.slides.length) % this.slides.length;
    this.lbIndex = n;
    const frame = this.lightbox.querySelector('[data-lb-frame]');
    const source = this.slides[n];
    const clone = source.cloneNode(true);
    clone.removeAttribute('data-slide');
    clone.querySelectorAll('[data-sound-pill], [data-sound-toggle]').forEach((el) => el.remove()); // native controls there
    clone.className = source.className.replace('gallery__slide', 'lightbox__slide');
    clone.querySelectorAll('img').forEach((img) => { img.loading = 'eager'; img.sizes = '100vh'; });
    frame.replaceChildren(clone);
    const video = clone.querySelector('video');
    if (video) {
      video.controls = true;
      if (!this.reduced.matches) {
        if (window.BBSound) window.BBSound.play(video); else video.play().catch(() => {});
      }
    }
    this.lightbox.querySelectorAll('[data-lb-thumb]').forEach((t) => {
      const on = Number(t.dataset.lbThumb) === n;
      t.classList.toggle('is-active', on);
      if (on) t.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
